<template>
  <div class="rmr-page-shell social-crawlers-page">
    <rmr-page-hero
      title="Crawlers"
      title-accent="Sociaux"
      subtitle="Relance manuelle des collectes Reporting Social et Revenus"
      :icon="icons.mdiDatabaseSyncOutline"
    >
      <template #actions>
        <social-reporting-health />
      </template>
    </rmr-page-hero>

    <section class="rmr-page-shell__content social-crawlers-page__content">
      <v-card flat class="rmr-table-shell social-crawlers-card">
        <rmr-stepper
          v-model="step"
          :steps="['Crawler', 'Période', 'Confirmation']"
        >
          <v-stepper-content :step="1" class="social-crawlers-step">
            <div class="social-crawlers-step__heading">
              <div class="rmr-table-shell__eyebrow">Étape 1</div>
              <div class="rmr-table-shell__title">Sélectionner un crawler</div>
              <p class="social-crawlers-step__description">
                Choisissez la collecte à lancer manuellement dans Airflow.
              </p>
            </div>

            <v-data-table
              v-model="selected"
              :headers="headers"
              :items="workflows"
              item-key="key"
              single-select
              show-select
              hide-default-footer
              class="rmr-data-table social-crawlers-table"
            >
              <template v-slot:item.name="{ item }">
                <span class="social-crawlers-table__name">
                  {{ item.name }}
                </span>
              </template>
            </v-data-table>
          </v-stepper-content>

          <v-stepper-content :step="2" class="social-crawlers-step">
            <div class="social-crawlers-step__heading">
              <div class="rmr-table-shell__eyebrow">Étape 2</div>
              <div class="rmr-table-shell__title">Configurer la période</div>
              <p class="social-crawlers-step__description">
                Définissez la période à collecter pour
                {{ selectedWorkflowName }}.
              </p>
            </div>

            <div class="social-crawlers-form">
              <div class="social-crawlers-form__field">
                <label class="rmr-table-shell__filter-label">Mode</label>
                <rmr-segmented-control
                  v-model="periodSelectionType"
                  :items="periodTypes"
                  size="md"
                  block
                />
              </div>

              <div class="social-crawlers-form__field">
                <label class="rmr-table-shell__filter-label">Période</label>
                <rmr-date-picker-field
                  v-if="periodSelectionType === 'one-day'"
                  v-model="day"
                  :display-value="day"
                  label="Sélectionner une date"
                  :prepend-inner-icon="icons.mdiCalendarTodayOutline"
                  :picker-props="{ max: maxDate }"
                  close-on-input
                />
                <rmr-date-picker-field
                  v-else
                  v-model="dates"
                  :display-value="dateRangeLabel"
                  label="Sélectionner une période"
                  :prepend-inner-icon="icons.mdiCalendarRange"
                  :picker-props="{ max: maxDate }"
                  range
                />
              </div>
            </div>

            <rmr-notice
              title="Période limitée"
              variant="info"
              class="social-crawlers-step__notice"
            >
              Chaque relance est limitée à un seul mois pour borner la durée
              et la taille du traitement. Pour un rattrapage, privilégiez une
              journée à la fois.
            </rmr-notice>
          </v-stepper-content>

          <v-stepper-content :step="3" class="social-crawlers-step">
            <div class="social-crawlers-step__heading">
              <div class="rmr-table-shell__eyebrow">Étape 3</div>
              <div class="rmr-table-shell__title">Confirmer la relance</div>
              <p class="social-crawlers-step__description">
                Vérifiez le périmètre avant de déclencher le traitement.
              </p>
            </div>

            <div class="social-crawlers-summary">
              <div class="social-crawlers-summary__icon">
                <v-icon :color="RMR_INDIGO" size="28">
                  {{ icons.mdiDatabaseSyncOutline }}
                </v-icon>
              </div>
              <div>
                <div class="social-crawlers-summary__title">
                  {{ selectedWorkflowName }}
                </div>
                <div class="social-crawlers-summary__period">
                  {{ periodSummary }}
                </div>
                <p class="social-crawlers-summary__description">
                  {{ selectedWorkflowDescription }}
                </p>
              </div>
            </div>

            <rmr-notice
              v-if="selectedWorkflowKey === 'revenue'"
              title="Ordre recommandé"
              variant="warning"
              class="social-crawlers-step__notice"
            >
              Pour une première initialisation, lancez d’abord le crawler
              Reporting Social afin de créer les comptes nécessaires.
            </rmr-notice>
          </v-stepper-content>
        </rmr-stepper>

        <div class="social-crawlers-card__actions">
          <rmr-action-button
            v-if="step > 1"
            label="Retour"
            :icon="icons.mdiArrowLeft"
            variant="ghost"
            :disabled="isLaunching"
            @click="step--"
          />
          <v-spacer />
          <rmr-action-button
            v-if="step < 3"
            label="Continuer"
            :icon="icons.mdiArrowRight"
            variant="primary"
            @click="gotoNextStep"
          />
          <rmr-action-button
            v-else
            label="Relancer le crawler"
            :icon="icons.mdiPlay"
            variant="primary"
            :loading="isLaunching"
            :disabled="isLaunching"
            @click="triggerCrawler"
          />
        </div>
      </v-card>
    </section>
  </div>
</template>

<script>
import axios from "@axios";
import {
  mdiArrowLeft,
  mdiArrowRight,
  mdiCalendarRange,
  mdiCalendarTodayOutline,
  mdiDatabaseSyncOutline,
  mdiPlay,
} from "@mdi/js";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";
import RmrDatePickerField from "@/components/ui/forms/RmrDatePickerField.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import RmrStepper from "@/components/ui/layout/RmrStepper.vue";
import SocialReportingHealth from "@/components/audience/social-reporting/SocialReportingHealth.vue";
import { RMR_INDIGO } from "@/styles/tokens";

export default {
  name: "SocialCrawlers",
  components: {
    RmrActionButton,
    RmrDatePickerField,
    RmrNotice,
    RmrPageHero,
    RmrSegmentedControl,
    RmrStepper,
    SocialReportingHealth,
  },
  data() {
    const yesterday = this.daysAgo(1);
    const monthStart = new Date(
      yesterday.getFullYear(),
      yesterday.getMonth(),
      1
    );
    return {
      step: 1,
      selected: [],
      isLaunching: false,
      periodSelectionType: "a-period",
      day: this.formatDate(yesterday),
      dates: [this.formatDate(monthStart), this.formatDate(yesterday)],
      periodTypes: [
        {
          value: "one-day",
          label: "Un jour",
          icon: mdiCalendarTodayOutline,
        },
        {
          value: "a-period",
          label: "Une période",
          icon: mdiCalendarRange,
        },
      ],
      workflows: [
        {
          key: "social_reporting",
          name: "Reporting Social",
          description:
            "Synchronise les comptes, les publications et leurs métriques depuis Nonli.",
        },
        {
          key: "revenue",
          name: "Revenus Facebook",
          description:
            "Recalcule les revenus Facebook depuis les données déjà collectées en base.",
        },
      ],
      headers: [
        {
          text: "Crawler",
          align: "start",
          sortable: true,
          value: "name",
        },
        {
          text: "Description",
          align: "start",
          sortable: false,
          value: "description",
        },
      ],
      icons: {
        mdiArrowLeft,
        mdiArrowRight,
        mdiCalendarRange,
        mdiCalendarTodayOutline,
        mdiDatabaseSyncOutline,
        mdiPlay,
      },
      RMR_INDIGO,
    };
  },
  computed: {
    selectedWorkflow() {
      return this.selected.length ? this.selected[0] : null;
    },
    selectedWorkflowKey() {
      return this.selectedWorkflow ? this.selectedWorkflow.key : "";
    },
    selectedWorkflowName() {
      return this.selectedWorkflow ? this.selectedWorkflow.name : "";
    },
    selectedWorkflowDescription() {
      return this.selectedWorkflow ? this.selectedWorkflow.description : "";
    },
    maxDate() {
      return this.formatDate(this.daysAgo(1));
    },
    normalizedDates() {
      return [...this.dates].sort();
    },
    dateRangeLabel() {
      return this.normalizedDates.join(" - ");
    },
    dateStart() {
      return this.periodSelectionType === "one-day"
        ? this.day
        : this.normalizedDates[0];
    },
    dateEnd() {
      return this.periodSelectionType === "one-day"
        ? this.day
        : this.normalizedDates[1];
    },
    formErrors() {
      const errors = [];
      if (this.periodSelectionType === "one-day" && !this.day) {
        errors.push("Veuillez sélectionner une date.");
      }
      if (this.periodSelectionType === "a-period" && this.dates.length !== 2) {
        errors.push("Veuillez sélectionner une période complète.");
      }
      if (
        this.dateStart &&
        this.dateEnd &&
        this.dateStart.slice(0, 7) !== this.dateEnd.slice(0, 7)
      ) {
        errors.push("La période doit rester dans un seul mois calendaire.");
      }
      return errors;
    },
    periodSummary() {
      if (this.dateStart === this.dateEnd) {
        return `Pour la journée du ${this.dateStart}`;
      }
      return `Pour la période du ${this.dateStart} au ${this.dateEnd}`;
    },
  },
  methods: {
    daysAgo(numberOfDays) {
      const value = new Date();
      value.setDate(value.getDate() - numberOfDays);
      return value;
    },
    formatDate(value) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async showMessage(text, color = "warning") {
      await this.$store.dispatch("app/setSnackBar", {
        show: true,
        text,
        color,
        timeout: 5000,
      });
    },
    async gotoNextStep() {
      if (this.step === 1 && !this.selectedWorkflow) {
        await this.showMessage("Veuillez sélectionner un crawler.");
        return;
      }
      if (this.step === 2 && this.formErrors.length) {
        await this.showMessage(this.formErrors.join(" "));
        return;
      }
      this.step += 1;
    },
    async triggerCrawler() {
      this.isLaunching = true;
      try {
        await axios.post("/audience/social-reporting/crawlers/run", {
          crawler: this.selectedWorkflowKey,
          date_from: this.dateStart,
          date_to: this.dateEnd,
        });
        await this.showMessage(
          `Crawler ${this.selectedWorkflowName} lancé dans Airflow.`,
          "success"
        );
        this.step = 1;
        this.selected = [];
      } catch (error) {
        const detail = error.response && error.response.data?.detail;
        await this.showMessage(
          typeof detail === "string"
            ? detail
            : `Erreur lors du lancement du crawler ${this.selectedWorkflowName}.`
        );
      } finally {
        this.isLaunching = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.social-crawlers-page__content {
  padding-bottom: $rmr-space-xl;
}

.social-crawlers-step {
  padding: $rmr-space-xl !important;
}

.social-crawlers-step__heading {
  margin-bottom: $rmr-space-lg;
}

.social-crawlers-step__description,
.social-crawlers-summary__description {
  margin: 6px 0 0;
  color: $rmr-text-muted;
  font-size: $rmr-fs-body;
}

.social-crawlers-table__name {
  color: $rmr-indigo-dark;
  font-weight: 800;
}

.social-crawlers-form {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(280px, 1fr);
  gap: $rmr-space-xl;
  max-width: 860px;
}

.social-crawlers-step__notice {
  margin-top: $rmr-space-lg;
}

.social-crawlers-summary {
  display: flex;
  gap: $rmr-space-md;
  width: 100%;
  padding: $rmr-space-lg;
  box-sizing: border-box;
  background: $rmr-indigo-bg;
  border: 1px solid $rmr-indigo-border;
  border-radius: $rmr-radius-xl;
}

.social-crawlers-summary__icon {
  display: flex;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: $rmr-surface-0;
  border-radius: $rmr-radius-lg;
}

.social-crawlers-summary__title {
  color: $rmr-indigo-dark;
  font-size: 17px;
  font-weight: 800;
}

.social-crawlers-summary__period {
  margin-top: 3px;
  color: $rmr-text-secondary;
  font-size: $rmr-fs-md;
  font-weight: 700;
}

.social-crawlers-card__actions {
  display: flex;
  align-items: center;
  min-height: 70px;
  padding: $rmr-space-md $rmr-space-xl;
  border-top: 1px solid $rmr-border-hover;
}

@media (max-width: 720px) {
  .social-crawlers-form {
    grid-template-columns: 1fr;
  }

  .social-crawlers-step {
    padding: $rmr-space-md !important;
  }
}
</style>
